import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Send, CheckCircle } from "lucide-react";

const ContactForm = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const form = e.target;
    const formData = new FormData(form);

    try {
      const response = await fetch("https://formspree.io/f/xqebqpkl", {
        method: "POST",
        body: formData,
        headers: {
          Accept: "application/json",
        },
      });

      if (response.ok) {
        setSubmitted(true);
        form.reset();
      }
    } catch (error) {
      console.error("Form submission error:", error);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Card className="shadow-xl rounded-2xl border-2 max-w-2xl mx-auto" data-testid="contact-form-success">
        <CardContent className="p-12 text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-teal-400 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Thank you!</h3>
          <p className="text-lg text-gray-700 mb-6">
            Our team will get in touch with you.
          </p>
          <Button
            onClick={() => setSubmitted(false)}
            variant="outline"
            className="border-teal-600 text-teal-700 hover:bg-teal-50"
            data-testid="submit-another-btn"
          >
            Submit Another Request
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-xl rounded-2xl border-2 max-w-2xl mx-auto" data-testid="contact-form">
      <CardHeader className="bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-t-2xl">
        <CardTitle className="text-2xl md:text-3xl">Get in Touch</CardTitle>
        <CardDescription className="text-blue-50 text-base">
          Tell us about your health goals and we&apos;ll reach out to discuss how we can help
        </CardDescription>
      </CardHeader>
      <CardContent className="p-6 md:p-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Name Field */}
          <div>
            <Label htmlFor="name" className="text-gray-700 font-medium">
              Full Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              name="name"
              data-testid="contact-name-input"
              placeholder="Enter your full name"
              required
              className="mt-2"
            />
          </div>

          {/* Age Field */}
          <div>
            <Label htmlFor="age" className="text-gray-700 font-medium">
              Age <span className="text-red-500">*</span>
            </Label>
            <Input
              id="age"
              name="age"
              type="number"
              data-testid="contact-age-input"
              placeholder="Enter your age"
              required
              min="1"
              max="120"
              className="mt-2"
            />
          </div>

          {/* Email Field */}
          <div>
            <Label htmlFor="email" className="text-gray-700 font-medium">
              Email <span className="text-red-500">*</span>
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              data-testid="contact-email-input"
              placeholder="your.email@example.com"
              required
              className="mt-2"
            />
          </div>

          {/* Phone Field */}
          <div>
            <Label htmlFor="phone" className="text-gray-700 font-medium">
              Phone <span className="text-red-500">*</span>
            </Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              data-testid="contact-phone-input"
              placeholder="+91 XXXXX XXXXX"
              required
              className="mt-2"
            />
          </div>

          {/* Service Interest Dropdown - Native HTML Select for Formspree */}
          <div>
            <Label htmlFor="service" className="text-gray-700 font-medium">
              Service Interested In <span className="text-red-500">*</span>
            </Label>
            <select
              id="service"
              name="service"
              data-testid="contact-service-select"
              required
              className="mt-2 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              defaultValue=""
            >
              <option value="" disabled>Select a service</option>
              <option value="LessMed RationalRx™ – Prescription Review">
                LessMed RationalRx™ – Prescription Review
              </option>
              <option value="LessMed Precision Panel™ – Supplement Guidance">
                LessMed Precision Panel™ – Supplement Guidance
              </option>
            </select>
          </div>

          {/* Description Field (Optional) */}
          <div>
            <Label htmlFor="description" className="text-gray-700 font-medium">
              Short Description of Concern <span className="text-gray-500 text-sm">(optional)</span>
            </Label>
            <Textarea
              id="description"
              name="description"
              data-testid="contact-description-textarea"
              placeholder="Briefly describe your health concern or what you'd like help with..."
              rows={4}
              className="mt-2 resize-none"
            />
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            data-testid="contact-submit-btn"
            disabled={loading}
            className="w-full bg-gradient-to-r from-teal-500 to-blue-600 hover:from-teal-600 hover:to-blue-700 text-white py-6 text-lg rounded-full shadow-lg transition-all hover:shadow-xl hover:scale-[1.02]"
          >
            {loading ? (
              "Sending..."
            ) : (
              <>
                <Send className="w-5 h-5 mr-2" />
                Send Message
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default ContactForm;
